import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  NgZone,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, from, of } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslationService } from '@alfresco/adf-core';
import { ContentApiService } from '@alfresco/aca-shared';
import { SiteEntry } from '@alfresco/js-api';
import { ProfileState } from '@alfresco/adf-extensions';
import * as LDClient from 'launchdarkly-js-client-sdk';

import { AppStore } from '../../store/states/app.state';
import {
  GetFeatureFlags,
  SetCurrentSiteAction,
  SetSiteChildrenAction,
  SetSiteManagerAction,
  SetSitesAction,
  SetUserRoleForCurrentSiteAction,
} from '../../store/actions/app.custom-actions';
import { WkSiteModel } from '../../model/wk-site.model';
import { WkSiteContainerModel } from '../../model/wk-site-children.model';
import {
  currentSiteSelector,
  getUserProfile,
  sitesSelector,
} from '../../store/selectors/app.custom-selectors';
import { NodeInfoService } from '../../services/node.service';
import { CONSTANTS } from '../../model/custom-adf-model';
import { ContentService } from '../shared/services/content.service';
import { WkAppConfigService } from '../../services/wk-app-config.service';
import { LDClientService } from '../../services/ldclient.service';
import { MouseflowService } from '../../services/mouseflow.service';

@Component({
  selector: 'wk-current-site',
  templateUrl: './wk-current-site.component.html',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'wk-current-site' },
  standalone: false,
})
export class WkCurrentSiteComponent implements OnInit, OnDestroy {
  sites = [] as WkSiteModel[];
  siteName = 'My sites';
  siteGuid: string;
  siteRefId: string;
  isAnySiteAvailable = true;
  broadcastChannel: BroadcastChannel;
  currentSiteValue = 'currentSite';
  private readonly _onDestroy = new Subject<void>();
  private userProfile: ProfileState;

  constructor(
    private readonly store: Store<AppStore>,
    private readonly wkContentService: ContentService,
    private readonly translationService: TranslationService,
    private readonly router: Router,
    private readonly nodeInfoService: NodeInfoService,
    private readonly ngZone: NgZone,
    private readonly contentApiService: ContentApiService,
    private readonly configService: WkAppConfigService,
    private readonly ldClient: LDClientService,
    private readonly mouseflowService: MouseflowService
  ) {
    // Initialise Mouseflow once — reads the website ID from app.config.json.
    // Add "mouseflowWebsiteId": "<your-website-id>" to app.config.json.
    const mouseflowWebsiteId =
      this.configService.get<string>('mouseflowWebsiteId');
    this.mouseflowService.initialize(mouseflowWebsiteId);

    this.store
      .select(getUserProfile)
      .pipe(takeUntil(this._onDestroy))
      .subscribe((state) => {
        if (state) {
          this.userProfile = state;

          // Identify the user in Mouseflow by their opaque ID (non-PII).
          this.mouseflowService.identifyUser(state?.id);

          this.ldClient.identify({
            kind: 'user',
            key: state?.id,
            custom: {
              groups: state?.groups?.map((group) => group.id) ?? [],
            },
          });
        }
      });
  }

  ngOnInit() {
    this.broadcastChannel = this.wkContentService.getBroadcastChannel();
    this.broadcastChannel.onmessage = (event) =>
      this.ngZone.run(() => this.setDataAndNavigate({ id: event.data } as any));
    // fetches the list of sites from store.
    // this code should be broken into different observables to adhere to reactive programming.
    this.store
      .select(sitesSelector)
      .pipe(
        switchMap((sites: WkSiteModel[]) => {
          if (!sites || !sites.length) {
            // if store does not contain the list of sites, then fetch from source
            return from(this.wkContentService.getSites()).pipe(
              tap((sitesList: WkSiteModel[]) => {
                if (!sitesList || !sitesList.length) {
                  this.siteName = this.translationService.instant(
                    'APP.SITE.NO_SITE_MESSAGE'
                  );
                  this.store.dispatch(new SetCurrentSiteAction('no-site'));
                  this.isAnySiteAvailable = false;
                } else {
                  this.store.dispatch(new SetSitesAction(sitesList));
                }
              })
            );
          }
          return of(sites);
        }),
        filter((sites: WkSiteModel[]) => sites && !!sites.length),
        tap((sites: WkSiteModel[]) => {
          this.sites = sites;
        }),
        switchMap(() =>
          this.store.select(currentSiteSelector).pipe(
            // if Library is created then we don't want to get the old value from store until new
            // list of libraries are fetched
            filter(() => !this.wkContentService.libraryCreated),
            tap((currentSite: string) => {
              if (currentSite === '') {
                const isSiteStorredLocally = localStorage.getItem(
                  this.currentSiteValue
                );
                if (isSiteStorredLocally) {
                  this.store.dispatch(
                    new SetCurrentSiteAction(isSiteStorredLocally)
                  );
                } else {
                  this.getLastLoginSite();
                }
              }
            }),
            filter(
              (currentSite) => currentSite !== '' && currentSite !== 'no-site'
            )
          )
        ),
        takeUntil(this._onDestroy)
      )
      .subscribe((currentSite: string) => {
        const siteDetails = this.getSiteDetails(currentSite);
        if (siteDetails) {
          this.store.dispatch(new SetSiteManagerAction(siteDetails.role));
          this.siteName = siteDetails.title;
          this.siteGuid = siteDetails.guid;
          this.getContainersForSelectedSite(this.siteGuid);
          // Track the active site so Mouseflow sessions can be filtered by site.
          this.mouseflowService.trackSiteContext(
            siteDetails.id,
            siteDetails.title
          );
        } else {
          this.store.dispatch(new SetCurrentSiteAction(this.sites[0].id));
        }
        this.getCurrentSiteUserRole(currentSite);
      });
  }

  getSiteDetails(currentSite: string) {
    return this.sites.find((ele: WkSiteModel) => ele.id === currentSite);
  }

  selectSite(site: WkSiteModel) {
    this.removeDataFromStorage();
    this.broadcastChannel.postMessage(site.id);
    // Tag the Mouseflow session whenever the user explicitly switches site.
    this.mouseflowService.trackSiteSelected(site.id, site.title);
    this.setDataAndNavigate(site);
    this.store.dispatch(
      new GetFeatureFlags({
        person: this.userProfile as any,
        groups: this.userProfile?.groups?.map((group) => group.id) ?? [],
      })
    );
  }

  /**
   * Fetches containers of a given site and updates the store.
   *
   * @param siteguid site guid to fetch the containers
   */
  getContainersForSelectedSite(siteguid: string) {
    this.wkContentService
      .getSiteContainers(siteguid)
      .pipe(takeUntil(this._onDestroy))
      .subscribe((data: WkSiteContainerModel[]) => {
        this.store.dispatch(new SetSiteChildrenAction(data));
      });
  }

  ngOnDestroy() {
    this._onDestroy.next();
  }

  getLastLoginSite() {
    this.wkContentService
      .getLastSiteLoggedIn()
      .pipe(takeUntil(this._onDestroy))
      .subscribe((site: string) => {
        const siteId = site === 'no-site' ? this.sites[0].id : site;
        localStorage.setItem(this.currentSiteValue, siteId);
        this.store.dispatch(new SetCurrentSiteAction(siteId));
      });
  }

  removeDataFromStorage() {
    if (this.router.url.includes('dashboard')) {
      this.nodeInfoService.setCurrentNode(null);
    }
    if (localStorage.getItem(CONSTANTS.DOCUMENT_LIBRARY)) {
      localStorage.removeItem(CONSTANTS.DOCUMENT_LIBRARY);
    }
    if (localStorage.getItem(CONSTANTS.EMAIL_TEMPLATES)) {
      localStorage.removeItem(CONSTANTS.EMAIL_TEMPLATES);
    }
    if (localStorage.getItem(CONSTANTS.CONTENT_TEMPLATES)) {
      localStorage.removeItem(CONSTANTS.CONTENT_TEMPLATES);
    }
    if (localStorage.getItem(CONSTANTS.CONTRACT_TEMPLATES)) {
      localStorage.removeItem(CONSTANTS.CONTRACT_TEMPLATES);
    }
  }

  setDataAndNavigate(site: WkSiteModel) {
    localStorage.setItem(this.currentSiteValue, site.id);
    this.store.dispatch(new SetCurrentSiteAction(site.id));
    const context: LDClient.LDContext = {
      kind: 'multi',
      organization: {
        key: this.configService.get('ldcInfo.tenantKey'),
        name: this.configService.get('ldcInfo.environmentKey'),
        siteId: site.id,
      },
    };
    this.ldClient.identify(context);
    // Push a virtual pageview so the dashboard visit is tracked as a
    // distinct page in Mouseflow Session Replay and Heatmaps.
    this.mouseflowService.newPageView('/dashboard');
    this.router.navigate(['dashboard']);
  }

  getCurrentSiteUserRole(currentSite: string) {
    this.contentApiService
      .getSite(currentSite)
      .subscribe((siteEntry: SiteEntry) => {
        this.store.dispatch(
          new SetUserRoleForCurrentSiteAction(siteEntry.entry.role)
        );
      });
  }
}
